import { useState } from "react";
import { taskService } from "@miplatform/api";
import { DATE_TIME_FORMATS, HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import { IInspectionAttempt } from "@miplatform/types";
import { createUUID, getDateISO, getFormattedDate } from "@miplatform/utils";

const getInspectionAttemptUUID = createUUID();
const cancelInspectionAttemptUUID = createUUID();
const scheduleInspectionAttemptUUID = createUUID();
const greenTagInspectionAttemptUUID = createUUID();
const redTagInspectionAttemptUUID = createUUID();

export const useInspectionAttempt = () => {
  const stateManager = useComponentState();
  const [inspectionAttempt, setInspectionAttempt] =
    useState<IInspectionAttempt | null>(null);

  const getInspectionAttempt = async (id: string) => {
    stateManager.abort(getInspectionAttemptUUID);
    return stateManager.run(async () => {
      const response = await taskService.inspection.getAttemptById({ id });
      if (response.status >= HTTP_STATUSES.OK) {
        setInspectionAttempt(response.data);
      }
      return response;
    }, getInspectionAttemptUUID);
  };

  const scheduleInspectionAttempt = async ({
    attemptId,
    scheduledDate,
    buttonProps,
  }: {
    attemptId: string;
    scheduledDate: string;
    buttonProps?: { title: string; onClick: () => void } | null;
  }) => {
    stateManager.abort(scheduleInspectionAttemptUUID);
    return stateManager.run(
      async () =>
        taskService.inspection.schedule({
          attemptId,
          scheduledDate: getDateISO(scheduledDate),
          toastMessageConfig: {
            success: {
              text: `Inspection scheduled for ${getFormattedDate(
                DATE_TIME_FORMATS.FORMAT_MM_DD_YY,
                scheduledDate
              )}.`,
              buttonProps,
            },
          },
        }),
      scheduleInspectionAttemptUUID
    );
  };

  const greenTagInspectionAttempt = async ({
    id,
    inspectedDate,
    reportedDate,
  }: {
    id: string;
    inspectedDate?: string;
    reportedDate?: string;
  }) => {
    stateManager.abort(greenTagInspectionAttemptUUID);
    return stateManager.run(
      async () =>
        taskService.inspection.pass({
          id,
          data: {
            inspectedDate: getDateISO(inspectedDate),
            reportedDate: getDateISO(reportedDate),
          },
          toastMessageConfig: {
            success: {
              text: "Green tag was created successfully.",
            },
          },
        }),
      greenTagInspectionAttemptUUID
    );
  };

  const redTagInspectionAttempt = async ({
    id,
    inspectedDate,
    reportedDate,
  }: {
    id: string;
    inspectedDate?: string;
    reportedDate?: string;
  }) => {
    stateManager.abort(redTagInspectionAttemptUUID);
    return stateManager.run(
      async () =>
        taskService.inspection.fail({
          id,
          data: {
            inspectedDate: getDateISO(inspectedDate),
            reportedDate: getDateISO(reportedDate),
          },
          toastMessageConfig: {
            success: {
              text: "Red tag was created successfully.",
            },
          },
        }),
      redTagInspectionAttemptUUID
    );
  };

  const cancelInspectionAttempt = async ({
    id,
    reason,
  }: {
    id: string;
    reason: string;
  }) => {
    stateManager.abort(cancelInspectionAttemptUUID);
    return stateManager.run(
      async () =>
        taskService.inspection.cancel({
          id,
          data: { reason },
          toastMessageConfig: {
            success: { text: "Inspection attempt was cancelled successfully." },
          },
        }),
      cancelInspectionAttemptUUID
    );
  };

  const clear = () => {
    stateManager.abort(getInspectionAttemptUUID);
    setInspectionAttempt(null);
  };

  return {
    getInspectionAttempt,
    inspectionAttempt,
    setInspectionAttempt,
    scheduleInspectionAttempt,
    greenTagInspectionAttempt,
    redTagInspectionAttempt,
    cancelInspectionAttempt,
    isLoading: stateManager.isBusy(),
    clear,
  };
};
